/**
 * Customers — the MSP portal landing.
 *
 * This is the top of the funnel for a multi-tenant product, so the question it answers is
 * not "what customers do I have" but "which one needs me right now".
 *
 * WHY THIS IS A TABLE AND NOT A CARD GRID. It was a grid of cards, which reads well at three
 * customers and fails at thirty: no search, no sort, one screen per handful of rows, and the
 * one customer with a problem looks exactly like the twenty that are fine. An MSP does not
 * browse this list, it TRIAGES it. So:
 *
 *   1. A SUMMARY LINE ANSWERS "is anything wrong" WITHOUT SCANNING. Customers needing
 *      attention, devices offline and open alerts are totalled above the table, because at
 *      200 rows the answer must not depend on reading 200 rows.
 *   2. THE DEFAULT SORT IS EDITORIAL. Worst health first, then most alerts, then most
 *      offline. Whatever sits at the top is what gets looked at, so alphabetical order --
 *      which correlates with nothing -- would bury the thing that matters. (Same reasoning
 *      as the device list opening on recency; see DataGrid's initialSorting note.)
 *   3. FILTER BEFORE SCROLL. "Needs attention" is one click and is the view an operator
 *      actually works from; search handles the "I know who I want" case.
 *
 * Below `lg` DataGrid reflows to cards on its own, so the small-screen experience keeps the
 * density where density is possible and stays readable where it is not.
 *
 * Selecting a customer sets the session org scope and navigates into that org's health
 * landing — the rest of the app then follows the chosen scope.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import type { ColumnDef } from "@tanstack/react-table";
import { useCustomers, useCreateCustomer } from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { DataGrid } from "@/components/data/DataGrid";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { cn } from "@/lib/cn";
import { CountLink } from "@/components/data/CountLink";
import { alertsHref, offlineDevicesHref } from "@/lib/alertGuide";
import type { Customer } from "@/types/domain";
import { QUARANTINE_LABEL, isQuarantineOrg, orgLabel } from "@/lib/quarantine";

/** Worst first. The order the table opens on, and the order the filter counts assume. */
const HEALTH_RANK: Record<Customer["health"], number> = { critical: 0, warn: 1, healthy: 2 };

type Filter = "all" | "attention" | "quarantine";

/** A customer is "needing attention" if anything about it is not clean. */
function needsAttention(c: Customer): boolean {
  return c.health !== "healthy" || c.alerts_open > 0 || c.devices_offline > 0;
}

export default function Customers() {
  const { data, isLoading, error } = useCustomers();
  const setOrg = useSession((s) => s.setOrg);
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const customers = useMemo(() => data?.items ?? [], [data]);

  const totals = useMemo(() => {
    return {
      // The quarantine holding area is not a customer: it is not counted as one (2026-10-19).
      count: customers.filter((c) => !isQuarantineOrg(c)).length,
      attention: customers.filter((c) => !isQuarantineOrg(c) && needsAttention(c)).length,
      offline: customers.reduce((n, c) => n + c.devices_offline, 0),
      alerts: customers.reduce((n, c) => n + c.alerts_open, 0),
      quarantine: customers.filter((c) => c.is_quarantine).length,
    };
  }, [customers]);

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return customers
      .filter((c) => {
        if (filter === "attention" && !needsAttention(c)) return false;
        if (filter === "quarantine" && !c.is_quarantine) return false;
        if (!term) return true;
        return (
          c.name.toLowerCase().includes(term) ||
          (orgLabel(c) ?? "").toLowerCase().includes(term) ||
          (c.slug ?? "").toLowerCase().includes(term)
        );
      })
      // Pre-sorted worst-first so the table's initial view is already triaged; DataGrid's
      // own sorting takes over from the operator's first column click.
      .slice()
      .sort(
        (a, b) =>
          HEALTH_RANK[a.health] - HEALTH_RANK[b.health] ||
          b.alerts_open - a.alerts_open ||
          b.devices_offline - a.devices_offline ||
          a.name.localeCompare(b.name),
      );
  }, [customers, query, filter]);

  const open = (c: Customer) => {
    setOrg(c.id); // keep the store in sync so ScopeBar + queries follow
    navigate(`/o/${c.id}`);
  };

  const columns = useMemo<ColumnDef<Customer, unknown>[]>(
    () => [
      {
        id: "customer",
        header: "Customer",
        accessorFn: (c) => c.name,
        cell: (ctx) => {
          const c = ctx.row.original;
          return (
            <div className="flex min-w-0 items-center gap-2">
              {/* A severity stripe reads before any number does. */}
              <span
                aria-hidden
                className={cn(
                  "h-5 w-1 shrink-0 rounded-full",
                  c.health === "critical"
                    ? "bg-critical"
                    : c.health === "warn"
                      ? "bg-warn"
                      : "bg-healthy/40",
                )}
              />
              <div className="min-w-0">
                {/* Holding area: the label, with the stored name as secondary text. */}
                <div className="truncate font-medium text-ink">{orgLabel(c)}</div>
                {isQuarantineOrg(c) ? (
                  <div className="truncate text-xs text-ink-subtle">Holding area · stored as {c.name}</div>
                ) : (
                  c.slug && <div className="truncate text-xs text-ink-subtle">{c.slug}</div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "health",
        header: "Health",
        accessorFn: (c) => HEALTH_RANK[c.health],
        cell: (ctx) => {
          const c = ctx.row.original;
          // A pill that is red/amber because of alerts opens those alerts (OP#1854). A pill that
          // is amber only because devices are offline opens the offline devices instead.
          const to =
            c.alerts_open > 0
              ? alertsHref(c.id, null, (c.alerts_critical ?? 0) > 0 ? { severity: "critical" } : {})
              : c.devices_offline > 0
                ? offlineDevicesHref(c.id)
                : null;
          if (!to) return <StatusPill status={c.health} />;
          return (
            <Link
              to={to}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              className="inline-flex rounded"
              title={c.alerts_open > 0 ? `Open alerts for ${c.name}` : `Offline devices for ${c.name}`}
            >
              <StatusPill status={c.health} />
            </Link>
          );
        },
      },
      {
        id: "sites",
        header: "Sites",
        accessorFn: (c) => c.sites_count,
        // A holding area has no customer sites: its placeholder is not shown as "1".
        cell: (ctx) =>
          isQuarantineOrg(ctx.row.original) ? (
            <span className="text-ink-subtle" title="Holding area: placeholder sites are not counted">
              —
            </span>
          ) : (
            <span className="tabular-nums text-ink">{ctx.row.original.sites_count}</span>
          ),
      },
      {
        id: "devices",
        header: "Devices",
        accessorFn: (c) => c.devices_total,
        cell: (ctx) => {
          const c = ctx.row.original;
          return (
            <span className="tabular-nums text-ink">
              {c.devices_online}
              <span className="text-ink-subtle">/{c.devices_total}</span>
            </span>
          );
        },
      },
      {
        id: "offline",
        header: "Offline",
        accessorFn: (c) => c.devices_offline,
        cell: (ctx) => {
          const c = ctx.row.original;
          // Zero recedes; a count opens the Devices list filtered to offline (OP#1854).
          return (
            <CountLink
              n={c.devices_offline}
              to={offlineDevicesHref(c.id)}
              tone="warn"
              label={`offline devices for ${c.name}`}
            />
          );
        },
      },
      {
        id: "alerts",
        header: "Alerts",
        accessorFn: (c) => c.alerts_open,
        cell: (ctx) => {
          const c = ctx.row.original;
          // The count opens the customer's open-alerts list (OP#1854).
          return (
            <CountLink
              n={c.alerts_open}
              to={alertsHref(c.id)}
              tone={c.health === "critical" ? "critical" : "warn"}
              label={`open alerts for ${c.name}`}
            />
          );
        },
      },
    ],
    [],
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Customers" subtitle="Every customer you manage" />
        {/* Only msp_admin may create tenants; the backend re-checks (403). */}
        <RoleGate min="msp_admin" mode="hide">
          <AddCustomer />
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}

      {customers.length > 0 && (
        <>
          <TriageBar totals={totals} />

          <div className="mb-3 mt-3 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="cust-search">
              Search customers
            </label>
            <input
              id="cust-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search customers"
              className="w-full max-w-xs rounded border border-line bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-subtle"
            />
            <div className="flex items-center gap-1">
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
                All {customers.length}
              </FilterChip>
              <FilterChip
                active={filter === "attention"}
                onClick={() => setFilter("attention")}
                tone={totals.attention > 0 ? "warn" : undefined}
              >
                Needs attention {totals.attention}
              </FilterChip>
              {totals.quarantine > 0 && (
                <FilterChip
                  active={filter === "quarantine"}
                  onClick={() => setFilter("quarantine")}
                >
                  {QUARANTINE_LABEL}
                </FilterChip>
              )}
            </div>
            {rows.length !== customers.length && (
              <span className="text-xs text-ink-muted">
                {rows.length} of {customers.length}
              </span>
            )}
          </div>
        </>
      )}

      {data && customers.length === 0 ? (
        <EmptyState
          title="No customers yet"
          hint="Create a customer to start managing its sites and devices."
        />
      ) : (
        <DataGrid
          data={rows}
          columns={columns}
          rowKey={(c) => c.id}
          dense
          isLoading={isLoading}
          onRowActivate={open}
          initialSorting={[{ id: "health", desc: false }]}
          empty={
            <span>
              {filter === "attention"
                ? "Every customer is healthy."
                : "No customer matches that search."}
            </span>
          }
          renderCard={(c) => <CustomerCard customer={c} />}
        />
      )}
    </div>
  );
}

/**
 * The one line that answers "is anything wrong" without reading the table.
 *
 * Deliberately shows nothing but a calm confirmation when everything is clean — a row of
 * zeroes in alert colours trains people to ignore the colours.
 */
function TriageBar({
  totals,
}: {
  totals: { count: number; attention: number; offline: number; alerts: number };
}) {
  if (totals.attention === 0) {
    return (
      <p role="status" className="mt-3 text-sm text-ink-muted">
        All {totals.count} customers healthy — nothing offline, no open alerts.
      </p>
    );
  }
  return (
    <p role="status" className="mt-3 text-sm text-ink">
      <span className="font-medium text-warn">{totals.attention}</span>
      <span className="text-ink-muted">
        {totals.attention === 1 ? " customer needs" : " customers need"} attention
      </span>
      {totals.offline > 0 && (
        <>
          <span className="text-ink-subtle"> · </span>
          <span className="font-medium text-warn tabular-nums">{totals.offline}</span>
          <span className="text-ink-muted"> devices offline</span>
        </>
      )}
      {totals.alerts > 0 && (
        <>
          <span className="text-ink-subtle"> · </span>
          <span className="font-medium text-warn tabular-nums">{totals.alerts}</span>
          <span className="text-ink-muted"> open alerts</span>
        </>
      )}
    </p>
  );
}

function FilterChip({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone?: "warn";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs transition-colors",
        active
          ? "border-brand bg-brand/10 text-brand"
          : "border-line text-ink-muted hover:bg-surface-2",
        !active && tone === "warn" && "text-warn",
      )}
    >
      {children}
    </button>
  );
}

/** The sub-`lg` card DataGrid reflows to. Same facts, stacked. */
function CustomerCard({ customer }: { customer: Customer }) {
  const c = customer;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{orgLabel(c)}</div>
          <div className="mt-0.5 text-xs text-ink-muted">
            {isQuarantineOrg(c)
              ? "Holding area"
              : `${c.sites_count} site${c.sites_count === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <StatusPill status={c.health} />
        </div>
      </div>
      <dl className="grid grid-cols-3 gap-2">
        <Metric label="Devices" value={`${c.devices_online}/${c.devices_total}`} />
        <Metric
          label="Offline"
          value={c.devices_offline}
          tone={c.devices_offline > 0 ? "warn" : undefined}
          to={offlineDevicesHref(c.id)}
          linkLabel={`offline devices for ${c.name}`}
        />
        <Metric
          label="Alerts"
          value={c.alerts_open}
          tone={c.alerts_open > 0 ? (c.health === "critical" ? "critical" : "warn") : undefined}
          to={alertsHref(c.id)}
          linkLabel={`open alerts for ${c.name}`}
        />
      </dl>
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
  to,
  linkLabel,
}: {
  label: string;
  value: number | string;
  tone?: "healthy" | "warn" | "critical";
  /** When set and the value is a non-zero number, the value links here (OP#1854). */
  to?: string;
  linkLabel?: string;
}) {
  if (to && typeof value === "number" && (tone === "warn" || tone === "critical")) {
    return (
      <div>
        <dt className="text-xs text-ink-muted">{label}</dt>
        <dd className="mt-0.5 text-lg font-semibold">
          <CountLink n={value} to={to} tone={tone} label={linkLabel ?? label} />
        </dd>
      </div>
    );
  }
  const toneClass =
    tone === "healthy"
      ? "text-healthy"
      : tone === "warn"
        ? "text-warn"
        : tone === "critical"
          ? "text-critical"
          : "text-ink";
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className={`mt-0.5 text-lg font-semibold tabular-nums ${toneClass}`}>{value}</dd>
    </div>
  );
}

/** "New customer" button + a small create dialog (name, optional slug). */
function AddCustomer() {
  // Auto-open when arrived via "+ New customer…" from the ScopeBar (/customers?new=1); clear
  // the flag on close so a refresh/back doesn't re-pop the dialog.
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const create = useCreateCustomer();

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o && params.get("new")) {
      params.delete("new");
      setParams(params, { replace: true });
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), ...(slug.trim() ? { slug: slug.trim() } : {}) },
      {
        onSuccess: () => {
          onOpenChange(false);
          setName("");
          setSlug("");
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild>
        <Button variant="primary">New customer</Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">New customer</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Create a customer to manage its sites and devices.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Name</span>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Hotels"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Slug (optional)</span>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="acme"
                className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
              />
            </label>
            {create.isError && (
              <span className="text-xs text-critical">Could not create the customer.</span>
            )}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Dialog.Close asChild>
                <Button variant="ghost" type="button">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button variant="primary" type="submit" disabled={!name.trim() || create.isPending}>
                {create.isPending ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
